import { Theme } from '@mui/material/styles';

/**
 * Recolours an SVG illustration (imported with `?raw`) to the selected theme
 * and returns it as a data URI usable as an <img src> or a CSS url().
 *
 * The illustrations were drawn in the old Tailwind palette (slate greys,
 * white, blues / indigos / cyans, greens, pinks). Each hex colour is mapped
 * by role, keeping its lightness so the drawing keeps its shading:
 *   - neutrals (white → slate-900)  → blend between surface and ink, so a
 *     white card stays a "paper" shape and dark lines stay "ink" in both
 *     light and dark themes;
 *   - blue / indigo / cyan          → the theme accent hue;
 *   - green, pink / violet          → the theme's second accent hue;
 *   - red / orange / amber          → kept (they mean warning / negative);
 *   - pale tints of any hue (L>85%) → a faint wash of the mapped hue over
 *     the surface, as the original pastels were washes over white.
 */

type Rgb = [number, number, number];
type Hsl = [number, number, number];

const parseHex = (hex: string): Rgb | null => {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Parses the #hex / rgb() strings MUI palettes hold. */
const parseColor = (color: string): Rgb => {
  if (color.startsWith('#')) return parseHex(color) ?? [0, 0, 0];
  const m = color.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const [r, g, b] = m[1].split(',').map((v) => parseFloat(v));
    return [r, g, b];
  }
  return [0, 0, 0];
};

const toHex = ([r, g, b]: Rgb) =>
  '#' + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');

const rgbToHsl = ([r, g, b]: Rgb): Hsl => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, s, l];
};

const hslToRgb = ([h, s, l]: Hsl): Rgb => {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0) * 255, f(8) * 255, f(4) * 255];
};

const mix = (a: Rgb, b: Rgb, t: number): Rgb =>
  [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

export const themeSvgColor = (hex: string, theme: Theme): string => {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const [h, s, l] = rgbToHsl(rgb);
  const surface = parseColor(theme.palette.background.paper);
  const ink = parseColor(theme.palette.text.primary);

  // Neutrals: white = paper, slate-900 = ink. Tailwind's slates are slightly
  // blue (saturation up to ~0.5 at the dark end), so low saturation, or a
  // muted colour near black/white, counts as neutral rather than brand.
  if (s < 0.3 || (s < 0.5 && (l < 0.25 || l > 0.8))) {
    return toHex(mix(ink, surface, l));
  }

  const accentHsl = rgbToHsl(parseColor(theme.palette.primary.main));
  const accent2Hsl = rgbToHsl(parseColor(theme.palette.secondary.main));
  // Red / orange / amber keep their meaning (warning / negative).
  const warm = h < 50 || h >= 345;
  const isAccentFamily = h >= 170 && h < 265;
  const target = isAccentFamily ? accentHsl : accent2Hsl;
  const mapped: Rgb = warm
    ? rgb
    : hslToRgb([target[0], Math.max(target[1], 0.35), Math.min(Math.max(l, 0.3), 0.72)]);

  // Pale tints (incl. warm ones like amber-100): a faint wash of the hue over
  // the surface, as the originals were washes over white.
  if (l > 0.85) {
    const base = warm ? hslToRgb([h, s, 0.5]) : mapped;
    return toHex(mix(surface, base, 0.18));
  }
  return warm ? hex : toHex(mapped);
};

const cache = new Map<string, string>();

/** The SVG recoloured to `theme`, as a data URI. */
export const themedSvgUrl = (source: string, theme: Theme): string => {
  const key = `${theme.palette.primary.main}|${theme.palette.secondary.main}|${theme.palette.background.paper}|${theme.palette.text.primary}|${source.length}|${source.slice(0, 64)}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const recoloured = source
    .replace(/#[0-9a-fA-F]{6}\b|#[0-9a-fA-F]{3}\b/g, (hex) => themeSvgColor(hex, theme))
    .replace(/(fill|stroke|stop-color)="white"/gi, (_m, attr) => `${attr}="${themeSvgColor('#ffffff', theme)}"`);
  // Also escape ( ) ' — encodeURIComponent leaves them, and callers drop the
  // URI into an unquoted CSS url(...), where a ")" would end it early.
  const encoded = encodeURIComponent(recoloured).replace(/[()']/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
  const url = `data:image/svg+xml;charset=utf-8,${encoded}`;
  cache.set(key, url);
  return url;
};
